export const PAYMENT_PROVIDER_ERROR_MESSAGE =
    "We are having trouble reaching our payment provider. Please try again in a few minutes.";

/**
 * Error thrown for a non-2xx API response. `code` is the backend's machine-readable
 * `error` field when present (e.g. "payment_provider_unavailable").
 */
export class ApiRequestError extends Error {
    constructor(
        message: string,
        public readonly status: number,
        public readonly code?: string
    ) {
        super(message);
        this.name = "ApiRequestError";
    }
}

interface ErrorBody {
    error?: string;
    message?: string;
}

/**
 * Pick a message that is safe to show the user. Server messages are trusted for 4xx
 * responses and for curated errors (those with an `error` code); a bare 5xx message
 * such as Laravel's "Server Error" falls back to the friendly default.
 */
function messageFromBody(
    status: number,
    body: ErrorBody | null,
    fallback: string
): string {
    const isCurated = typeof body?.error === "string";
    if (body?.message && (isCurated || status < 500)) {
        return body.message;
    }
    return fallback;
}

/**
 * Build an ApiRequestError from a non-2xx status and its (possibly null) JSON body.
 */
export function toApiRequestError(
    status: number,
    body: ErrorBody | null,
    fallback: string = PAYMENT_PROVIDER_ERROR_MESSAGE
): ApiRequestError {
    return new ApiRequestError(
        messageFromBody(status, body, fallback),
        status,
        body?.error
    );
}

/**
 * Parse a fetch() Response as JSON, throwing an ApiRequestError when it is not 2xx.
 */
export async function jsonOrThrow<T = any>(
    response: Response,
    fallback: string = PAYMENT_PROVIDER_ERROR_MESSAGE
): Promise<T> {
    if (response.ok) {
        return (await response.json()) as T;
    }

    let body: ErrorBody | null = null;
    try {
        body = await response.json();
    } catch {
        // Non-JSON error page (e.g. gateway timeout HTML) — use the fallback message.
    }

    throw toApiRequestError(response.status, body, fallback);
}

/**
 * Extract a user-facing message from anything thrown by fetch/axios/our helpers.
 */
export function getApiErrorMessage(
    error: unknown,
    fallback: string = PAYMENT_PROVIDER_ERROR_MESSAGE
): string {
    if (error instanceof ApiRequestError) {
        return error.message || fallback;
    }

    const axiosResponse = (
        error as { response?: { status?: number; data?: ErrorBody } }
    )?.response;
    if (axiosResponse?.status) {
        return messageFromBody(
            axiosResponse.status,
            axiosResponse.data ?? null,
            fallback
        );
    }

    return fallback;
}
